// NetworkBuilder.jsx  — React front‑end for Rodin «Build‑network» API
// Реализует реальный детерминированный прогресс‑бар через task_id + polling
// ===========================================================================

import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Button,
  Typography,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  Grow
} from '@mui/material';
import axios from 'axios';

import DataUploadWithType from './DataUploadWithType';
import ParametersForm from './ParametersForm';
import NetworkPlot from './NetworkPlot';

const API = 'https://api.netan.io/api/build-network/';

const formatMetric = (value, digits = 4) => (
  typeof value === 'number' ? value.toFixed(digits) : (value ?? '?')
);
const formatMaybeMetric = (value, digits = 4) => {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(digits) : value;
};

const csvCell = (value) => {
  const s = String(value ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const orderedDataHeaders = (rows, sampleColumns = []) => {
  const headers = [];
  rows.forEach(row => {
    Object.keys(row).forEach(key => {
      if (!headers.includes(key)) headers.push(key);
    });
  });
  const sampleSet = new Set(sampleColumns);
  const presentSamples = sampleColumns.filter(col => headers.includes(col));
  return [
    ...headers.filter(col => !sampleSet.has(col)),
    ...presentSamples,
  ];
};

const NetworkBuilder = () => {
  // ───────── Local state ───────────────────────────────────────────────────
  const [filesData, setFilesData] = useState([]);   // [{file, type}, …]
  const [syncAll, setSyncAll] = useState(true);
  const [parameters, setParameters] = useState({}); // Form values

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);      // 0–100
  const [taskId, setTaskId] = useState(null);       // uuid from server

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [buildStats, setBuildStats] = useState(null);

  const [dataTable, setDataTable] = useState([]);
  const [dataSampleColumns, setDataSampleColumns] = useState([]);

  const [errorMessage, setErrorMessage] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);

  const pollRef = useRef(null);                     // interval id

  // ───────── Handlers (file / params) ──────────────────────────────────────
  const handleFilesChange      = (info) => setFilesData(info);
  const handleSyncChange       = (v)   => setSyncAll(v);
  const handleParametersChange = (p)   => setParameters(p);

  const canBuild =
    filesData.length > 0 && filesData.every(f => f.file && f.type);

  // ───────── Polling helpers ───────────────────────────────────────────────
  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  

  const pollProgress = (id) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}?task_id=${id}`);
        setProgress(data.progress ?? 0);
  
        if (data.status === 'done') {
          setNodes(data.result.nodes || []);
          setEdges(data.result.edges || []);
          if (data.result.dataTable) {
            setDataTable(data.result.dataTable);
          }
          setDataSampleColumns(data.result.dataSampleColumns || []);
          if (data.result.stats) setBuildStats(data.result.stats);
  
          setLoading(false);
          stopPolling();
        } else if (data.status === 'error') {
          throw new Error(data.error || 'Unknown server error');
        } else if (data.status === 'cancelled' || data.status === 'cancelling') {
          setLoading(false);
          stopPolling();
        }
      } catch (err) {
        setErrorMessage(err.message);
        setErrorDialogOpen(true);
        setLoading(false);
        stopPolling();
      }
    }, 2500); // poll every 2.5 s
  };

  // ───────── Build button click ────────────────────────────────────────────
  const handleBuildNetwork = async () => {
    stopPolling();
    setLoading(true);
    setProgress(0);
    setNodes([]);
    setEdges([]);
    setBuildStats(null);
    setDataTable([]);
    setDataSampleColumns([]);

    const formData = new FormData();
    filesData.forEach(f => {
      formData.append('data_files', f.file);
      formData.append('file_types', f.type);
    });
    formData.append('parameters',
      JSON.stringify({ syncAll, paramData: parameters }));

    try {
      const res = await axios.post(API, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const id = res.data.task_id;
      setTaskId(id);
      pollProgress(id);
    } catch (err) {
      setErrorMessage(
        err.response?.data?.error ? err.response.data.error : err.message
      );
      setErrorDialogOpen(true);
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      await axios.patch(`${API}?task_id=${taskId}&action=cancel`);
    } catch (err) { /* тихо игнорируем */ }
    stopPolling();
    setLoading(false);
    setProgress(0);
    setTaskId(null);
    setNodes([]); setEdges([]); setBuildStats(null);
    setDataTable([]);
    setDataSampleColumns([]);
  };

  // clear polling on component unmount
  useEffect(() => stopPolling, []);

  useEffect(() => {
    const abortOnUnload = () => {
      if (!taskId) return;
      const url = `${API}?task_id=${taskId}&action=cancel`;
      // пустой Blob – GET‑запрос; можно отправить FormData → будет POST
      navigator.sendBeacon(url);
    };
    window.addEventListener('beforeunload', abortOnUnload);
    return () => window.removeEventListener('beforeunload', abortOnUnload);
  }, [taskId]);

  // ───────── Build‑stats pretty printer (same as ваш оригинал) ─────────────
  const getStatsText = () => {
    if (!buildStats) return '';
    const lines = [];

    /* per‑file */
    if (Array.isArray(buildStats.fileStats)) {
      buildStats.fileStats.forEach((s, i) => {
        const orig    = s.features_original        ?? 0;
        const prep    = s.features_after_preproc   ?? orig;
        const remPrep = s.features_removed_preproc ?? orig - prep;

        const beforeFil = s.features_before_filter    ?? prep;
        const afterFil  = s.features_after_filter     ?? beforeFil;
        const remFil    = s.features_removed_filter   ?? beforeFil - afterFil;

        const shape = s.X_shape ? `(${s.X_shape.join('×')})` : '(?)';

        lines.push(
          `File #${i + 1}: ` +
          `original=${orig}, ` +
          `after preproc=${prep}(‑${remPrep}), ` +
          `after filter=${afterFil}(‑${remFil}), ` +
          `X.shape=${shape}`
        );
      });
    }

    /* network global */
    const net = buildStats.networkStats || {};
    lines.push('', 'Network Stats:');
    lines.push(` - Total nodes: ${net.numNodes ?? '?'}`);
    lines.push(` - Edges: ${net.numEdges ?? '?'}`);
    lines.push(` - Nodes w/ edges: ${net.numNodesWithEdges ?? '?'}`);
    lines.push(` - Density: ${formatMetric(net.density)}`);
    lines.push(` - Mean degree: ${formatMetric(net.meanDegree)}`);
    lines.push(` - Components: ${net.numComponents ?? '?'}`);
    lines.push(` - Communities: ${net.numCommunities ?? '?'}, modules=${net.numModules ?? '?'}`);
    lines.push(
      ` - Thresholds: raw=${formatMaybeMetric(net.thresholdRaw)}, ` +
      `norm=${formatMaybeMetric(net.thresholdNorm)}, auto=${formatMaybeMetric(net.autoTarget)}, ` +
      `k=${formatMaybeMetric(net.kFinal)}`
    );

    /* per‑layer */
    const layerDict = {}; // {layer:{nodes,edges,density,communities,modules}}
    Object.entries(net).forEach(([k, v]) => {
      let m, lname;
      if ((m = k.match(/^nodes_(.+)/))) {
        lname = m[1];
        layerDict[lname] = { ...(layerDict[lname] || {}), nodes: v };
      } else if ((m = k.match(/^edges_(.+)/))) {
        lname = m[1];
        layerDict[lname] = { ...(layerDict[lname] || {}), edges: v };
      } else if ((m = k.match(/^density_(.+)/))) {
        lname = m[1];
        layerDict[lname] = {
          ...(layerDict[lname] || {}),
          density: formatMaybeMetric(v)
        };
      } else if ((m = k.match(/^communities_(.+)/))) {
        lname = m[1];
        layerDict[lname] = { ...(layerDict[lname] || {}), communities: v };
      } else if ((m = k.match(/^modules_(.+)/))) {
        lname = m[1];
        layerDict[lname] = { ...(layerDict[lname] || {}), modules: v };
      } else if ((m = k.match(/^meanDegree_(.+)/))) {
        lname = m[1];
        layerDict[lname] = {
          ...(layerDict[lname] || {}),
          meanDegree: formatMaybeMetric(v)
        };
      } else if ((m = k.match(/^meanDegreeActive_(.+)/))) {
        lname = m[1];
        layerDict[lname] = {
          ...(layerDict[lname] || {}),
          meanDegreeActive: formatMaybeMetric(v)
        };
      } else if ((m = k.match(/^thrRaw_(.+)/))) {
        lname = m[1];
        layerDict[lname] = { ...(layerDict[lname] || {}), thrRaw: formatMaybeMetric(v) };
      } else if ((m = k.match(/^thrNorm_(.+)/))) {
        lname = m[1];
        layerDict[lname] = { ...(layerDict[lname] || {}), thrNorm: formatMaybeMetric(v) };
      } else if ((m = k.match(/^auto_(.+)/))) {
        lname = m[1];
        layerDict[lname] = { ...(layerDict[lname] || {}), auto: formatMaybeMetric(v) };
      } else if ((m = k.match(/^k_(.+)/))) {
        lname = m[1];
        layerDict[lname] = { ...(layerDict[lname] || {}), k: formatMaybeMetric(v) };
      }
    });

    if (net.layerMode === 'multilayer' && Object.keys(layerDict).length) {
      lines.push('', 'Per‑layer Stats:');
      Object.entries(layerDict).forEach(([l, s]) => {
        const label = l.replace(/_/g, ' ');
        lines.push(
          ` • ${label}: nodes=${s.nodes ?? '?'}, edges=${s.edges ?? '?'}, ` +
          `density=${s.density ?? '?'}, mean_degree=${s.meanDegree ?? '?'}, ` +
          `communities=${s.communities ?? '?'}, modules=${s.modules ?? '?'}, ` +
          `thr_raw=${s.thrRaw ?? '-'}, thr_norm=${s.thrNorm ?? '-'}, ` +
          `auto=${s.auto ?? '-'}, k=${s.k ?? '-'}`
        );
      });
    }

    return lines.join('\n');
  };

  // ───────── Download CSV (edges) ──────────────────────────────────────────
  const handleDownloadNetwork = () => {
    if (!edges.length) return;
  
    // добавляем compound-колонки только если они есть (features-mode)
    const hasComp = edges.some(e => ('source_compound' in e) || ('target_compound' in e));
  
    const header = ['source', 'target', 'weight', 'layer', 'layers']
      .concat(hasComp ? ['source_compound', 'target_compound'] : []);
    const lines = [header.join(',')];
  
    edges.forEach(e => {
      const src   = e.source;
      const tgt   = e.target;
      const w     = e.weight ?? 1;
      const layer = e.layer ?? '';
      const lays  = Array.isArray(e.layers) ? e.layers.join('|') : (e.layers ?? '');
  
      const row = [src, tgt, w, layer, lays];
      if (hasComp) row.push(e.source_compound ?? '', e.target_compound ?? '');
  
      lines.push(row.map(csvCell).join(','));
    });
  
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
  
    const a = document.createElement('a');
    a.href = url;
    a.download = 'network_netan.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  

  const handleDownloadData = () => {
    if (!dataTable.length) return;
  
    const headers = orderedDataHeaders(dataTable, dataSampleColumns);
  
    // 2) Строим CSV-строки
    const lines = [];
    // Заголовок
    lines.push(headers.map(csvCell).join(','));
    // Данные
    dataTable.forEach(row => {
      const line = headers.map(h => csvCell(row[h])).join(',');
      lines.push(line);
    });
  
    // 3) Сохраняем как файл
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rodin_data.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  
  

  // ───────── JSX ───────────────────────────────────────────────────────────
  return (
    <Container maxWidth="md" sx={{ mt: 0, px: 0 }}>
      {/* Upload + Sync */}
      <DataUploadWithType
        onFilesChange={handleFilesChange}
        onSyncChange={handleSyncChange}
      />

      {/* Parameters form */}
      <ParametersForm
        syncAll={syncAll}
        files={filesData}
        onChangeParams={handleParametersChange}
      />

      {/* Build button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleBuildNetwork}
          disabled={!canBuild || loading}
          sx={(theme) => ({
            width: 180,
            fontSize: '1.08rem',
            fontWeight: 800,
            color: theme.palette.mode === 'light' ? '#fff8f2' : '#f7e7de',
            backgroundColor: theme.palette.mode === 'light' ? '#b5501d' : '#632c19',
            border: `1px solid ${theme.palette.mode === 'light' ? '#9f4318' : '#944521'}`,
            boxShadow:
              theme.palette.mode === 'light'
                ? '0 10px 24px rgba(109, 47, 18, 0.18)'
                : '0 12px 28px rgba(0, 0, 0, 0.42)',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'light' ? '#9b4419' : '#7a351f',
            },
            '&.Mui-disabled': {
              color: theme.palette.mode === 'light' ? '#8b786d' : '#8e7162',
              backgroundColor: theme.palette.mode === 'light' ? '#eadfd6' : '#2b1912',
              borderColor: theme.palette.mode === 'light' ? '#e1cbbd' : '#5e3b2c',
              boxShadow: 'none',
            },
          })}
        >
          {loading ? 'Building…' : 'Build'}
        </Button>
      </Box>

      {/* Linear progress (determinate) */}
      {loading && (
        <Box sx={{ width: '100%', mt: 3.5 }}>
          <LinearProgress
            color="secondary"
            variant="buffer"
            value={progress}
            valueBuffer={progress+5}
          />
          <Typography align="center" sx={{ mt: 0.5 }}>
            {progress.toFixed(0)} %
          </Typography>
        </Box>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleCancel}
            sx={{ textTransform: 'none', width: 140 }}
          >
            Cancel
          </Button>
        </Box>
      )}



      {/* Build Stats card */}
      <Grow in={Boolean(buildStats)} timeout={800}>
        <Box sx={{ mt: 4 }}>
          {buildStats && (
            <Card
              sx={(theme) => ({
                p: 2,
                backgroundColor: theme.palette.card.plot,
                maxWidth: 900,
                m: '0 auto'
              })}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: 'bold', mb: 2 }}
                gutterBottom
              >
                Build Stats
              </Typography>
              <Typography
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: '0.95rem',
                  lineHeight: 1.5
                }}
              >
                {getStatsText()}
              </Typography>
            </Card>
          )}
        </Box>
      </Grow>

      {/* Network plot + Download button */}
      <Grow in={nodes.length && edges.length} timeout={800}>
        <Box sx={{ mt: 4 }}>
          <NetworkPlot nodes={nodes} edges={edges} />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              color="secondary"
              variant="outlined"
              onClick={handleDownloadNetwork}
              sx={{ textTransform: 'none' }}
            >
              Download Network
            </Button>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={handleDownloadData}
              disabled={!dataTable.length}
              sx={{ textTransform: 'none' }}
            >
            Download Data
          </Button>
          </Box>
        </Box>
      </Grow>

      {/* Error dialog */}
      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
      >
        <DialogTitle>Error</DialogTitle>
        <DialogContent>{errorMessage}</DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NetworkBuilder;
